import {
  IframeCommunicationServiceImpl,
  IframeCommunicationDelegate,
} from './services/iframe-communication.service';
import { inject, singleton } from 'tsyringe';
import { LoggerService } from '../shared/services/logger.service';
import {
  kNameOfPromotionWindowService,
  IPromotionWindowService,
} from '../shared/interfaces/promotion-window-service.interface';
import {
  kNameOfUserActionService,
  IUserActionService,
} from '../shared/interfaces/user-action-service.interface';
import { PromotionDisplayDetails } from '../shared/models/promotion-display-details.model';
import { OWWindowListenerWrapper } from '../shared/wrappers/ow-window-listener-wrapper';
import { OWWindowsListenerDelegate } from '../odk-ts';
import { OWWrapper } from '../shared/wrappers/ow-wrapper';
import { TrackEvent } from '../shared/models/tracking-event.model';
import { MPtracking } from '../shared/utils/mixpanel';

const kPromotionWindowName = 'promotion';
// -----------------------------------------------------------------------------
@singleton()
export class PromotionController
  implements IframeCommunicationDelegate, OWWindowsListenerDelegate
{
  private readonly logger = LoggerService.getCategory('PromotionController');
  private activePromotionInfo: PromotionDisplayDetails;

  // ---------------------------------------------------------------------------
  public constructor(
    @inject(kNameOfPromotionWindowService)
    private readonly promotionWindowService: IPromotionWindowService,
    @inject(kNameOfUserActionService)
    private readonly userActionService: IUserActionService,
    private readonly iframeComsService: IframeCommunicationServiceImpl,
    private readonly owWindowListener: OWWindowListenerWrapper,
    private readonly owWrapper: OWWrapper,
  ) {
    this.registerListeners();
  }

  // ---------------------------------------------------------------------------
  public async run() {
    try {
      MPtracking.init();
      this.activePromotionInfo =
        this.promotionWindowService.getActivePromotion();

      this.logger.info(
        `Running active promotion: ${JSON.stringify(this.activePromotionInfo)}`,
      );

      const { url } = this.activePromotionInfo;

      if (this.isIframeAddressValid(url)) {
        this.setIframeInMainDiv(url);
      }
    } catch (ex) {
      this.logger.error('Error while loading active Promotion:', ex);
      this.userActionService.sendErrorTrackingEvent(this.activePromotionInfo);

      this.promotionWindowService.closePromotionWindow();
    }
  }

  // ---------------------------------------------------------------------------
  private async registerListeners() {
    this.iframeComsService.addListener(this);
    this.owWindowListener.addListener(this);
    this.unregisterListeners();
    this.owWindowListener.addListener(this);
  }

  // ---------------------------------------------------------------------------
  private async unregisterListeners() {
    this.owWindowListener.removeListener(this);
  }

  // ---------------------------------------------------------------------------
  private async isIframeAddressValid(url: any) {
    const response = await fetch(url, {
      method: 'HEAD',
    });
    if (!response.ok) {
      console.error(
        '[Promotion Window] Error loading iframeURL.',
        response.status,
        response.statusText,
      );
      return false;
    }

    return true;
  }

  // ---------------------------------------------------------------------------
  private setIframeInMainDiv(srcUrl: string) {
    try {
      const iframe = document.createElement('iframe');
      iframe.setAttribute(
        'style',
        'overflow:hidden;height:100%;width:100%;display:block;',
      );
      iframe.id = 'notif-iframe';
      iframe.setAttribute('src', srcUrl);
      document.getElementById('main').appendChild(iframe);
    } catch (e) {
      this.logger.error('Error setting iframe in main div', e);
    }
  }

  // ---------------------------------------------------------------------------
  private isAltF4patchNeeded(): boolean {
    try {
      const altF4PatchVersion = '0.198.0.0';
      const owVersion = this.owWrapper.getVersion();
      const compareResult = altF4PatchVersion.localeCompare(owVersion);
      const performPatch: boolean = compareResult > 0;

      this.logger.info(
        `Alt+F4 patch - should perform patch? - ${performPatch}`,
      );

      return performPatch;
    } catch (e) {
      this.logger.error(
        'Error checking if altF4 patch is required, Error details:',
        e,
      );
      return true;
    }
  }

  // ---------------------------------------------------------------------------
  // IframeCommunication Delegate
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  onClosePromotionRequested() {
    MPtracking.track(
      'promotion_close_clicked',
      {
        promotionId: this.activePromotionInfo.promotionId,
        gameId: this.activePromotionInfo.gameId,
      },
      { send_immediately: true },
    );
    this.userActionService.promotionDismissed(this.activePromotionInfo);
    this.promotionWindowService.closePromotionWindow();
  }

  // ---------------------------------------------------------------------------
  onPromotionOfferAccepted() {
    this.userActionService.promotionOfferApproved(this.activePromotionInfo);
    this.promotionWindowService.closePromotionWindow();
  }

  // ---------------------------------------------------------------------------
  onSendTrackingRequested(event: TrackEvent) {
    this.userActionService.sendTrackingEvent(event);
  }

  // ---------------------------------------------------------------------------
  onToggleTimeoutTimerRequested(event: any) {
    this.promotionWindowService.toggleTimeoutTimer(event);
  }

  // ---------------------------------------------------------------------------
  onRecommendationInView() {
    this.promotionWindowService.promotionBecameVisible();
    this.userActionService.promotionDisplayed(this.activePromotionInfo);
    MPtracking.track(
      'promotion_displayed',
      {
        promotionId: this.activePromotionInfo.promotionId,
        gameId: this.activePromotionInfo.gameId,
      },
      { send_immediately: true },
    );
  }

  // ---------------------------------------------------------------------------
  onOpenExternalUrl(url: string) {
    MPtracking.track(
      'promotion_CTA_clicked',
      {
        promotionId: this.activePromotionInfo.promotionId,
        gameId: this.activePromotionInfo.gameId,
      },
      { send_immediately: true },
    );

    window.open(url, '_blank');
    this.userActionService.OpenExternalUrl(url, this.activePromotionInfo);
    setTimeout(() => {
      this.promotionWindowService.closePromotionWindow();
    }, 2000);
    // this.promotionWindowService.closePromotionWindow();
  }

  // ---------------------------------------------------------------------------
  onOpenNotificationSettingsRequested() {
    this.userActionService.openNotificationSettings(this.activePromotionInfo);
    // this.promotionWindowService.closePromotionWindow();
  }

  // ---------------------------------------------------------------------------
  onTurnOffPromotionsRequested() {
    this.userActionService.turnOffPromotions(this.activePromotionInfo);
    this.promotionWindowService.closePromotionWindow();
  }

  // ---------------------------------------------------------------------------
  // OWWindowsListenerDelegate
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // This function is used as a fallback scenario for Overwolf clients
  // below v0.198.0.0, where a window listener is notified of an AltF4Blocked
  // event only if the window listener is in the same window on which alt+F4 is
  // performed.
  // The non fallback onAltF4Blocked function is implemented in the
  // PromotionWindowService of the background window.
  // ---------------------------------------------------------------------------
  onAltF4Blocked(windowName: string) {
    if (
      !windowName.endsWith(kPromotionWindowName) ||
      !this.isAltF4patchNeeded()
    ) {
      return;
    }

    this.logger.info(
      `received Alt+F4 key combination on windowId: ${windowName},` +
        ' closing window',
    );

    this.userActionService.promotionDismissed(this.activePromotionInfo);
    this.unregisterListeners();
    this.promotionWindowService.closePromotionWindow();
  }
}
